/**
 * Live-preview layer for the scan-pattern wizard. Renders the in-progress
 * polygon / line / vertex dots as the user clicks on the map during
 * `scanDrawMode`. Once the user finalizes (Enter on polygon, 2nd click on
 * line) or cancels (Esc), the draft is cleared and the layer's source
 * goes empty.
 *
 * The actual SWEEP waypoints (the snake-pattern output) get inserted via
 * addWaypointsBulk and render through the existing DockMissionRouteLayer —
 * this layer only owns the draft visualization.
 *
 * Same safe-pattern rules as the other dock-demo layers: setData runs
 * unconditionally on the source (no isStyleLoaded gate), addSource and
 * addLayer gate on isStyleLoaded, style.load and styledata listeners
 * re-install when external code wipes our state.
 */

import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';

const SRC_DRAFT = 'dock-demo-scan-draft-src';
const LAYER_FILL = 'dock-demo-scan-draft-fill-lyr';
const LAYER_LINE = 'dock-demo-scan-draft-line-lyr';
const LAYER_PTS = 'dock-demo-scan-draft-pts-lyr';

const ALL_LAYERS = [LAYER_FILL, LAYER_LINE, LAYER_PTS] as const;

function isMissing(map: MapboxMap): boolean {
  if (!map.getSource(SRC_DRAFT)) return true;
  for (const id of ALL_LAYERS) if (!map.getLayer(id)) return true;
  return false;
}

function buildDraftFeatures(
  mode: 'polygon' | 'line' | null,
  verts: [number, number][],
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (mode === 'polygon' && verts.length >= 3) {
    features.push({
      type: 'Feature',
      properties: { kind: 'polygon' },
      geometry: { type: 'Polygon', coordinates: [[...verts, verts[0]]] },
    });
  }
  if (verts.length >= 2) {
    features.push({
      type: 'Feature',
      properties: { kind: 'line' },
      geometry: { type: 'LineString', coordinates: verts },
    });
  }
  for (let i = 0; i < verts.length; i++) {
    features.push({
      type: 'Feature',
      properties: { kind: 'point', index: i },
      geometry: { type: 'Point', coordinates: verts[i] },
    });
  }
  return { type: 'FeatureCollection', features };
}

function ensureSourcesAndLayers(map: MapboxMap): void {
  if (!map.getSource(SRC_DRAFT)) {
    map.addSource(SRC_DRAFT, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getLayer(LAYER_FILL)) {
    // Translucent cyan fill so the draft scan area is visually distinct
    // from NFZ red and matches the cyan route line of the existing mission.
    map.addLayer({
      id: LAYER_FILL,
      type: 'fill',
      source: SRC_DRAFT,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'fill-color': '#22d3ee', 'fill-opacity': 0.18 },
    });
  }
  if (!map.getLayer(LAYER_LINE)) {
    map.addLayer({
      id: LAYER_LINE,
      type: 'line',
      source: SRC_DRAFT,
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: {
        'line-color': '#22d3ee',
        'line-width': 2,
        'line-dasharray': [2, 2],
        'line-opacity': 1,
      },
    });
  }
  if (!map.getLayer(LAYER_PTS)) {
    map.addLayer({
      id: LAYER_PTS,
      type: 'circle',
      source: SRC_DRAFT,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': 6,
        'circle-color': '#22d3ee',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
  }
}

function pinLayersToTop(map: MapboxMap): void {
  for (const id of ALL_LAYERS) {
    try {
      if (map.getLayer(id)) map.moveLayer(id);
    } catch {
      // ignore
    }
  }
}

function teardownSourcesAndLayers(map: MapboxMap): void {
  for (const id of [...ALL_LAYERS].reverse()) {
    try {
      if (map.getLayer(id)) map.removeLayer(id);
    } catch { /* ignore */ }
  }
  try {
    if (map.getSource(SRC_DRAFT)) map.removeSource(SRC_DRAFT);
  } catch { /* ignore */ }
}

export default function ScanDraftLayer() {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const scanDrawMode = useDockDemoStore((s) => s.scanDrawMode);
  const scanDrawVertices = useDockDemoStore((s) => s.scanDrawVertices);

  useEffect(() => {
    if (!map || !isMapReady) return;

    const reinstall = () => {
      ensureSourcesAndLayers(map);
      const s = useDockDemoStore.getState();
      const src = map.getSource(SRC_DRAFT);
      if (src && 'setData' in src) {
        src.setData(buildDraftFeatures(s.scanDrawMode, s.scanDrawVertices));
      }
      pinLayersToTop(map);
    };

    const repin = () => {
      if (isMissing(map)) reinstall();
      else pinLayersToTop(map);
    };

    if (map.isStyleLoaded()) reinstall();
    map.on('style.load', reinstall);
    map.on('styledata', repin);

    return () => {
      map.off('style.load', reinstall);
      map.off('styledata', repin);
      teardownSourcesAndLayers(map);
    };
  }, [map, isMapReady]);

  // Data update on every draft change.
  useEffect(() => {
    if (!map || !isMapReady) return;
    const styleLoaded = map.isStyleLoaded();
    if (styleLoaded) ensureSourcesAndLayers(map);
    const src = map.getSource(SRC_DRAFT);
    if (src && 'setData' in src) {
      try {
        src.setData(buildDraftFeatures(scanDrawMode, scanDrawVertices));
      } catch {
        /* style mid-mutation */
      }
    }
    if (styleLoaded) pinLayersToTop(map);
  }, [map, isMapReady, scanDrawMode, scanDrawVertices]);

  return null;
}
